import React, { useMemo, useState, useCallback, useRef } from 'react';
import { DataTable } from '../../components/organisms';
import { Button } from '../../components/atoms';
import { useSubscriptionPlans, useSubscriptionPlanActions } from '../../hooks/useSubscriptions';
import type { Column, TableAction } from '../../types/table';
import type {
  SubscriptionPlan,
  SubscriptionPlanCreateRequest,
  SubscriptionPlanUpdateRequest,
} from '../../types/api/subscription';
import {
  formatDuration,
  formatTier,
  formatPrice,
  getTierColor,
} from '../../types/api/subscription';
import toast from 'react-hot-toast';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { SubscriptionPlanFormModal } from '../../components/organisms/SubscriptionPlanFormModal';

export const SubscriptionPlanManagementPage: React.FC = () => {
  const { fetchSubscriptionPlans } = useSubscriptionPlans();
  const {
    createSubscriptionPlan,
    updateSubscriptionPlan,
    activateSubscriptionPlan,
    deactivateSubscriptionPlan,
    deleteSubscriptionPlan,
  } = useSubscriptionPlanActions();
  const refreshKeyRef = useRef(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedPlan(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsDeleteModalOpen(true);
  }, []);

  const handleActivate = useCallback(
    async (plan: SubscriptionPlan) => {
      try {
        const loadingToast = toast.loading('Activating plan...');
        await activateSubscriptionPlan(plan.planId);
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch (error: any) {
        // Error already handled in hook with toast
      }
    },
    [activateSubscriptionPlan, handleRefresh]
  );

  const handleDeactivate = useCallback(
    async (plan: SubscriptionPlan) => {
      try {
        const loadingToast = toast.loading('Deactivating plan...');
        await deactivateSubscriptionPlan(plan.planId);
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch (error: any) {
        // Error already handled in hook with toast
      }
    },
    [deactivateSubscriptionPlan, handleRefresh]
  );

  const handleCreateSubmit = useCallback(
    async (payload: SubscriptionPlanCreateRequest) => {
      try {
        await createSubscriptionPlan(payload);
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: any) {
        // Error already handled in hook with toast
      }
    },
    [createSubscriptionPlan, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (payload: SubscriptionPlanUpdateRequest) => {
      if (!selectedPlan) return;
      try {
        await updateSubscriptionPlan(selectedPlan.planId, payload);
        setIsEditModalOpen(false);
        setSelectedPlan(null);
        handleRefresh();
      } catch (error: any) {
        // Error already handled in hook with toast
      }
    },
    [selectedPlan, updateSubscriptionPlan, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedPlan) return;
    try {
      await deleteSubscriptionPlan(selectedPlan.planId);
      setIsDeleteModalOpen(false);
      setSelectedPlan(null);
      handleRefresh();
    } catch (error: any) {
      // Error already handled in hook with toast
    }
  }, [selectedPlan, deleteSubscriptionPlan, handleRefresh]);

  const columns = useMemo<Column<SubscriptionPlan>[]>(
    () => [
      {
        id: 'name',
        header: 'Plan Name',
        accessor: 'name',
        cell: (row) => (
          <div>
            <p className="text-sm font-semibold text-gray-900">{row.name}</p>
            {row.isFeatured && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                ⭐ Featured
              </span>
            )}
          </div>
        ),
        minWidth: '200px',
      },
      {
        id: 'durationMonths',
        header: 'Duration',
        accessor: 'durationMonths',
        cell: (row) => <span className="text-sm text-gray-700">{formatDuration(row.durationMonths)}</span>,
      },
      {
        id: 'tierGranted',
        header: 'Tier',
        accessor: 'tierGranted',
        cell: (row) => (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(row.tierGranted)}`}>
            {formatTier(row.tierGranted)}
          </span>
        ),
      },
      {
        id: 'price',
        header: 'Price',
        accessor: 'finalPrice',
        cell: (row) => (
          <div>
            <p className="text-sm font-semibold text-gray-900">{formatPrice(row.finalPrice, row.currency)}</p>
            {row.finalPrice < row.price && (
              <p className="text-xs text-gray-500 line-through">{formatPrice(row.price, row.currency)}</p>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
      {
        id: 'features',
        header: 'Features',
        accessor: 'features',
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            {row.features.downloadEnabled && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700" title="Downloads Enabled">
                📥
              </span>
            )}
            {row.features.hdEnabled && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700" title="HD Quality">
                HD
              </span>
            )}
            {row.features.uhdEnabled && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700" title="UHD/4K Quality">
                4K
              </span>
            )}
            {!row.features.adsEnabled && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700" title="Ad-Free">
                🚫📺
              </span>
            )}
            {row.features.offlineViewing && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700" title="Offline Viewing">
                📴
              </span>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<SubscriptionPlan>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      // {
      //   id: 'toggle-active',
      //   label: 'Toggle Status',
      //   variant: 'ghost',
      //   icon: (
      //     <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      //       <path
      //         fillRule="evenodd"
      //         d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
      //         clipRule="evenodd"
      //       />
      //     </svg>
      //   ),
      //   onClick: (row: SubscriptionPlan) => (row.isActive !== false ? handleDeactivate(row) : handleActivate(row)),
      // },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleActivate, handleDeactivate, handleDelete]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-sm text-gray-600 mt-1">Manage subscription plans and pricing</p>
        </div>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Create Plan
        </Button>
      </div>

      {/* Data Table */}
      <DataTable<SubscriptionPlan>
        columns={columns}
        fetchData={fetchSubscriptionPlans}
        actions={actions}
        searchPlaceholder="Search subscription plans..."
        key={refreshKeyRef.current}
      />

      {/* Create Modal */}
      <SubscriptionPlanFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit as any}
        mode="create"
      />

      {/* Edit Modal */}
      <SubscriptionPlanFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPlan(null);
        }}
        onSubmit={handleEditSubmit as any}
        mode="edit"
        plan={selectedPlan || undefined}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Subscription Plan"
        message={
          selectedPlan
            ? `Are you sure you want to delete "${selectedPlan.name}"? This action cannot be undone and the plan cannot have any subscriptions associated with it.`
            : ''
        }
      />
    </div>
  );
};

